import { IDatasourceDto } from "@kuika/kuika-cl-model";
import { FormInstance } from "antd/es/form";

/**
 * The name side of a code editor's bar. The name is held in the EDITOR's state, not read back
 * from the form: the field mounts only while renaming, and `getFieldsValue()` / `onFinish` hand
 * back mounted fields alone — a save that read the name from them would go out nameless.
 */

/** The four controls of one rename row, for the suites. */
export interface ICodeEditorRenameTestIds {
  input: string;
  edit: string;
  confirm: string;
  cancel: string;
}

/** `csharpActionName` → `csharpActionNameInput`, `…EditButton`, `…ConfirmButton`, `…CancelButton`. */
export const codeEditorRenameTestIds = (prefix: string): ICodeEditorRenameTestIds => ({
  input: `${prefix}Input`,
  edit: `${prefix}EditButton`,
  confirm: `${prefix}ConfirmButton`,
  cancel: `${prefix}CancelButton`
});

/**
 * Runs the name field's rules and resolves to what it holds, or `undefined` when a rule
 * refused it. The rules are async — a uniqueness check among them — so a confirm has to wait
 * for their answer; reading the field's errors right away sees none and lets a duplicate through.
 */
export const validateRenameField = async (
  form: FormInstance | null | undefined,
  fieldName: string
): Promise<string | undefined> => {
  if (!form) return undefined;
  const isValid: boolean = await form.validateFields([fieldName]).then(
    (): boolean => true,
    (): boolean => false
  );
  if (!isValid) return undefined;
  const value: unknown = form.getFieldValue(fieldName);
  return typeof value === "string" ? value : "";
};

/**
 * The name a datasource editor opens with: the record's own while it edits one, the name typed
 * in the create picker only while creating. The picker's name outlives a cancelled create, so
 * reading it for an existing record would rename that record to a stale name.
 */
export const getOpeningDatasourceName = (
  editingDatasource: IDatasourceDto | undefined,
  creatingName: string | undefined
): string => {
  if (editingDatasource) return editingDatasource.name ?? "";
  return creatingName ?? "";
};
